import type { CSSProperties, ReactNode } from "react";
import dayjs from "dayjs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { isWeekendDay, SHIFT_HOURS } from "../utils";
import { useHolidays } from "@/hooks/use-holidays";
import type {
  AvailabilityMap,
  ShiftAvailability
} from "@/hooks/use-resource-availability";

// Translucent red wash + diagonal hatch so the "unavailable" read survives greyscale /
// color-blindness and low-contrast displays (WOR-2187 accessibility note). Single-source the
// hatch geometry (45° / 3px stripes) so the overlay and the legend swatch can't drift; only the
// alpha varies per context.
export const unavailableHatch = (strong: number, faint: number): string =>
  `repeating-linear-gradient(45deg, rgba(220,38,38,${strong}) 0, rgba(220,38,38,${strong}) 3px, rgba(220,38,38,${faint}) 3px, rgba(220,38,38,${faint}) 6px)`;

// Two intensities: STRONG marks the actually-blocked shift; FAINT fills the other half of a
// partially-blocked day so the cell reads as one full red block while still distinguishing
// AM-only / PM-only / full-day (WOR-2187 two-tone).
const UNAVAILABLE_STYLE: CSSProperties = {
  backgroundImage: unavailableHatch(0.22, 0.07)
};
const UNAVAILABLE_TINT = "aero:bg-red-500/15";

const UNAVAILABLE_STYLE_FAINT: CSSProperties = {
  backgroundImage: unavailableHatch(0.1, 0.03)
};
const UNAVAILABLE_TINT_FAINT = "aero:bg-red-500/5";

type Shift = "am" | "pm";

const SHIFT_FULL_LABEL: Record<Shift, string> = {
  am: "Morning (AM)",
  pm: "Afternoon (PM)"
};

// Edge-accent treatment (WOR-2187): an unavailable cell gets ONE uniform faint wash (so it
// reads as a single full red block, no harsh seam) and a solid red bar on the LEFT edge marks
// the specifically-blocked shift — AM (top half), PM (bottom half), or full height when both.
const ACCENT_BAR_CLASS = "aero:absolute aero:left-0 aero:w-[3px] aero:bg-red-600";

/**
 * Builds the merged "which shift(s) + why" content for an unavailable region. When both
 * shifts are blocked it collapses to a single "Full day" entry (with one reason line when
 * both shifts share the same reason, or AM/PM-prefixed lines when they differ).
 */
export const buildUnavailableContent = (
  am: boolean,
  pm: boolean,
  amReason?: string | null,
  pmReason?: string | null
): { title: string; reasons: string[] } => {
  const title =
    am && pm ? "Full day" : am ? SHIFT_FULL_LABEL.am : SHIFT_FULL_LABEL.pm;

  let reasons: string[] = [];
  if (am && pm) {
    if (amReason && pmReason) {
      reasons =
        amReason === pmReason
          ? [amReason]
          : [`AM: ${amReason}`, `PM: ${pmReason}`];
    } else if (amReason) {
      reasons = [`AM: ${amReason}`];
    } else if (pmReason) {
      reasons = [`PM: ${pmReason}`];
    }
  } else {
    const reason = am ? amReason : pmReason;
    if (reason) reasons = [reason];
  }
  return { title, reasons };
};

/**
 * The red hover card itself ("Unavailable — <title>" + optional reason lines). `hideArrow` +
 * transparent TooltipContent so the inner red box fully controls the look (matches
 * ScheduleBarTooltip). `asChild` keeps the wrapped band as the trigger — no extra layout node.
 */
const UnavailableTooltipBase = ({
  title,
  reasons,
  children
}: {
  title: string;
  reasons?: string[];
  children: ReactNode;
}) => (
  <TooltipProvider delayDuration={300}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="top"
        hideArrow
        className="aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none"
      >
        <div className="aero:max-w-48 aero:rounded-md aero:bg-red-600 aero:px-3 aero:py-1.5 aero:text-left aero:text-xs aero:text-white aero:shadow-md">
          <p className="aero:font-semibold">Unavailable — {title}</p>
          {(reasons ?? []).map((reason, i) => (
            <p key={i} className="aero:mt-0.5 aero:opacity-90 aero:break-words">
              {reason}
            </p>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/**
 * AM/PM convenience wrapper: merges AM+PM into one tooltip (a "Full day" block when both are
 * out), pulling reasons from the backend schedule note (WOR-2187).
 */
const UnavailableTooltip = ({
  am,
  pm,
  amReason,
  pmReason,
  children
}: {
  am: boolean;
  pm: boolean;
  amReason?: string | null;
  pmReason?: string | null;
  children: ReactNode;
}) => {
  const { title, reasons } = buildUnavailableContent(am, pm, amReason, pmReason);
  return (
    <UnavailableTooltipBase title={title} reasons={reasons}>
      {children}
    </UnavailableTooltipBase>
  );
};

/**
 * Resolves AM/PM/OT (un)availability for one resource-day. Weekend (grey) and public
 * holiday (yellow) convey the non-working day through their OWN background and are NOT
 * additionally washed red. AM/PM default available (red only on an explicit IsAvailable=false
 * override). OT defaults UNAVAILABLE (red) and is only available when explicitly set —
 * `otUnavailable` is consumed by the day-view timeline only. (WOR-2187 R3.)
 */
const resolveUnavailable = (
  day: dayjs.ConfigType,
  availability: Map<string, ShiftAvailability> | undefined,
  holidays: ReadonlyMap<string, string>
): {
  am: boolean;
  pm: boolean;
  amReason?: string | null;
  pmReason?: string | null;
  otUnavailable: boolean;
} => {
  const key = dayjs(day).format("YYYY-MM-DD");
  if (isWeekendDay(day) || holidays.has(key))
    return { am: false, pm: false, otUnavailable: false };
  const slot = availability?.get(key);
  return {
    am: slot?.am === false,
    pm: slot?.pm === false,
    amReason: slot?.amReason,
    pmReason: slot?.pmReason,
    otUnavailable: slot?.ot !== true
  };
};

interface AvailabilityBandsProps {
  days: Date[];
  /** This resource's availability slice: "YYYY-MM-DD" -> {am, pm}. Absent day = available. */
  availability?: Map<string, ShiftAvailability>;
  /** Fixed column width (detail/100% zoom). Omit to flex-fill (spanning view). */
  colWidth?: number;
  /**
   * Split point for the AM/PM divider as a fraction of cell height (0..1).
   * Defaults to 0.5 (top half = AM, bottom half = PM). In the 100%-zoom detail
   * grid pass 12/24 so the divider lands on the 12:00 hour line.
   */
  splitFraction?: number;
}

/**
 * Per-resource AM/PM availability overlay for the technician/machine WEEK calendar.
 * Shares the z-0 background layer (mounted AFTER SpanningGridLines and BEFORE the bars,
 * so by paint order it sits above the day background and below the bars).
 * A day with any unavailability renders ONE full-height red block (two-tone: the blocked
 * shift's half strong, the other faint; both blocked → full strong) with a single hover
 * tooltip. Weekend/holiday are conveyed by their grey/yellow background and never redded
 * (WOR-2187 R3).
 */
export const AvailabilityBands = ({
  days,
  availability,
  colWidth,
  splitFraction = 0.5
}: AvailabilityBandsProps) => {
  const holidays = useHolidays();
  const topPct = `${Math.round(splitFraction * 100)}%`;
  const bottomPct = `${Math.round((1 - splitFraction) * 100)}%`;

  return (
    <div className="aero:absolute aero:inset-0 aero:flex aero:z-0 aero:pointer-events-none">
      {days.map((day) => {
        const { am, pm, amReason, pmReason } = resolveUnavailable(
          day,
          availability,
          holidays
        );
        const columnStyle = colWidth ? { width: colWidth } : { flex: 1 };

        // Fully available → empty column (keeps day widths aligned).
        if (!am && !pm) {
          return (
            <div
              key={day.toISOString()}
              className="aero:flex-shrink-0"
              style={columnStyle}
            />
          );
        }

        // Any unavailability → one uniform full-height red block + a left-edge accent over the
        // blocked shift's half (full height when both), plus a single merged tooltip.
        return (
          <div
            key={day.toISOString()}
            className={`aero:flex-shrink-0 aero:relative ${UNAVAILABLE_TINT_FAINT}`}
            style={{ ...columnStyle, ...UNAVAILABLE_STYLE_FAINT }}
          >
            <UnavailableTooltip am={am} pm={pm} amReason={amReason} pmReason={pmReason}>
              <div className="aero:absolute aero:inset-0 aero:pointer-events-auto">
                {am && (
                  <div className={ACCENT_BAR_CLASS} style={{ top: 0, height: topPct }} />
                )}
                {pm && (
                  <div
                    className={ACCENT_BAR_CLASS}
                    style={{ bottom: 0, height: bottomPct }}
                  />
                )}
              </div>
            </UnavailableTooltip>
          </div>
        );
      })}
    </div>
  );
};

interface DayAvailabilityOverlayProps {
  /** The single day rendered in the Day (Timeline) view. */
  day: dayjs.ConfigType;
  /** This resource's availability slice: "YYYY-MM-DD" -> {am, pm}. */
  availability?: Map<string, ShiftAvailability>;
  /** Per-hour column width (getColWidth("daily", zoom)). */
  colWidth: number;
}

/**
 * Per-resource availability overlay for the technician/machine DAY (Timeline) view.
 * The day axis is time. AM (06:00–14:00) / PM (14:00–22:00) are washed red when unavailable.
 * OT — the [00:00–06:00) and [22:00–24:00) segments — is washed red BY DEFAULT (OT is
 * unavailable by default) and only left white when OT is explicitly available. Weekend/holiday
 * are conveyed by the day background and skipped here. Both OT segments are governed by the
 * displayed day's OT flag (the [00:00–06:00) carry-over from the previous day's OT shift is not
 * separately resolved). (WOR-2187.)
 */
export const DayAvailabilityOverlay = ({
  day,
  availability,
  colWidth
}: DayAvailabilityOverlayProps) => {
  const holidays = useHolidays();
  const { am, pm, amReason, pmReason, otUnavailable } = resolveUnavailable(
    day,
    availability,
    holidays
  );
  const { amStart, amEnd, pmStart, pmEnd } = SHIFT_HOURS;
  const DAY_END = 24;
  const region = `aero:absolute aero:top-0 aero:bottom-0 aero:pointer-events-auto ${UNAVAILABLE_TINT}`;

  // Skip entirely on weekend/holiday (day background conveys the non-working day).
  const key = dayjs(day).format("YYYY-MM-DD");
  if (isWeekendDay(day) || holidays.has(key)) return null;

  return (
    <div className="aero:absolute aero:inset-0 aero:z-0 aero:pointer-events-none">
      {/* OT carry-over [00:00–06:00) — red unless OT is available */}
      {otUnavailable && amStart > 0 && (
        <UnavailableTooltipBase title="Overtime (OT)">
          <div
            className={region}
            style={{ left: 0, width: amStart * colWidth, ...UNAVAILABLE_STYLE }}
          />
        </UnavailableTooltipBase>
      )}
      {am && (
        <UnavailableTooltip am pm={false} amReason={amReason}>
          <div
            className={region}
            style={{
              left: amStart * colWidth,
              width: (amEnd - amStart) * colWidth,
              ...UNAVAILABLE_STYLE
            }}
          />
        </UnavailableTooltip>
      )}
      {pm && (
        <UnavailableTooltip am={false} pm pmReason={pmReason}>
          <div
            className={region}
            style={{
              left: pmStart * colWidth,
              width: (pmEnd - pmStart) * colWidth,
              ...UNAVAILABLE_STYLE
            }}
          />
        </UnavailableTooltip>
      )}
      {/* OT evening [22:00–24:00) — red unless OT is available */}
      {otUnavailable && pmEnd < DAY_END && (
        <UnavailableTooltipBase title="Overtime (OT)">
          <div
            className={region}
            style={{
              left: pmEnd * colWidth,
              width: (DAY_END - pmEnd) * colWidth,
              ...UNAVAILABLE_STYLE
            }}
          />
        </UnavailableTooltipBase>
      )}
    </div>
  );
};

/** Aggregated AM/PM unavailability for one month day, with an optional per-shift reason. */
export interface MonthDayAvailability {
  am: boolean;
  pm: boolean;
  amReason?: string | null;
  pmReason?: string | null;
}

/**
 * Aggregates AM/PM unavailability for the merged MONTH calendar, which renders all
 * displayed resources' jobs in one date-centric grid (no per-resource rows). A shift
 * is marked unavailable only when EVERY displayed resource is unavailable for it
 * (intersection) — i.e. the day is genuinely blocked; filtered to a single resource
 * (e.g. via search) this is simply that resource's own availability. A reason is only
 * captured when exactly one resource is displayed (otherwise it is ambiguous, so the
 * tooltip falls back to the generic shift label). Weekend/holiday are conveyed by their
 * background and never redded. OT is excluded here (month has no hourly axis). (WOR-2187 R3.)
 */
export const resolveMonthAvailability = (
  days: Date[],
  resourceIds: number[],
  availabilityMap: AvailabilityMap | undefined,
  holidays: ReadonlyMap<string, string>
): Map<string, MonthDayAvailability> => {
  const out = new Map<string, MonthDayAvailability>();
  if (!availabilityMap || resourceIds.length === 0) return out;
  const singleId = resourceIds.length === 1 ? resourceIds[0] : null;
  for (const day of days) {
    const key = dayjs(day).format("YYYY-MM-DD");
    if (isWeekendDay(day) || holidays.has(key)) continue;
    let am = true;
    let pm = true;
    for (const id of resourceIds) {
      const slot = availabilityMap.get(id)?.get(key);
      if (slot?.am !== false) am = false;
      if (slot?.pm !== false) pm = false;
      if (!am && !pm) break;
    }
    if (am || pm) {
      const singleSlot =
        singleId != null ? availabilityMap.get(singleId)?.get(key) : undefined;
      out.set(key, {
        am,
        pm,
        amReason: am ? singleSlot?.amReason : null,
        pmReason: pm ? singleSlot?.pmReason : null
      });
    }
  }
  return out;
};

/**
 * Uniform full-block red wash + left-edge shift accent for a single MONTH day cell.
 * Sits behind the date label (z-0) and below the bars; renders nothing when the day is
 * fully available. Like the week view, the whole cell is one uniform faint wash and a solid
 * red bar on the left edge marks the blocked shift (AM top half / PM bottom half / full height
 * when both). PURE VISUAL (pointer-events-none): the month cell is itself a tooltip trigger, so
 * the reason is surfaced through that cell-level tooltip (see RightColumn). (WOR-2187 R3.)
 */
export const MonthDayUnavailableOverlay = ({ am, pm }: MonthDayAvailability) => {
  if (!am && !pm) return null;
  return (
    <div
      className={`aero:absolute aero:inset-0 aero:z-0 aero:pointer-events-none aero:overflow-hidden ${UNAVAILABLE_TINT_FAINT}`}
      style={UNAVAILABLE_STYLE_FAINT}
    >
      {am && <div className={ACCENT_BAR_CLASS} style={{ top: 0, height: "50%" }} />}
      {pm && <div className={ACCENT_BAR_CLASS} style={{ bottom: 0, height: "50%" }} />}
    </div>
  );
};

export default AvailabilityBands;
